import { useState, useEffect, useRef } from 'react';

/**
 * Tracks scroll progress and the currently active section in an ebook.
 * Active section is determined by which `<section id="...">` is closest
 * to the top of the viewport.
 */
export function useScrollProgress(chapters: { id: string }[]) {
  // The first chapter is treated as pre-content (e.g., the Introduction) and
  // never highlights in the TOC — readers shouldn't see a "you are here"
  // marker until they've actually entered chapter 1.
  const [activeSection, setActiveSection] = useState('');
  const progressRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  // Track scroll position for progress bar and active section
  useEffect(() => {
    const firstChapterId = chapters[0]?.id;
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

      // Update progress bar directly via DOM for instant feedback
      if (progressRef.current) {
        progressRef.current.style.width = `${scrollProgress}%`;
      }

      // Determine which section is active. Skip the first chapter so the
      // intro never gets a highlight — stays empty until the reader scrolls
      // into chapter 1+.
      const sections = document.querySelectorAll('section[id]');
      let current = '';
      sections.forEach((section) => {
        const id = section.getAttribute('id');
        if (!id || id === firstChapterId) return;
        const sectionTop = (section as HTMLElement).offsetTop;
        if (scrollTop >= sectionTop - 300) {
          current = id;
        }
      });
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [chapters]);

  // Auto-scroll the nav to keep the active chapter visible
  useEffect(() => {
    if (navRef.current) {
      if (!activeSection) {
        navRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const activeLink = navRef.current.querySelector(
          `[data-section="${activeSection}"]`,
        );
        if (activeLink) {
          activeLink.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
  }, [activeSection, chapters]);

  return { activeSection, progressRef, navRef };
}
